import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash-es/isEqual';
import pick from 'lodash-es/pick';

import { onChangeFilters } from 'ha/pages/Search/actions';
import { getConvertedPriceFilterParams } from 'ha/pages/Search/selectors/priceSelectors';
import {
  getFilterValues,
  getFiltersCount,
} from 'ha/pages/Search/selectors/filterSelectors';

import {
  DateFilterValue,
  TypeFilterValue,
  TypeValue,
  RoomsFilterValue,
  PriceFilterValue,
  FurnitureFilterValue,
  BillsFilterValue,
  SuitableForFilterValue,
  GenderFilterValue,
  RulesFilterValue,
  RegistrationFilterValue,
  RecentlyAddedFilterValue,
  ContractTypeFilterValue,
  SearchAreaFilterValue,
  PropertySizeFilterValue,
  FacilitiesFilterValue,
  AmenitiesFilterValue,
  RadiusFilterValue,
  AvailabilityFilterValue,
  SuitableForValue,
} from 'ha/types/SearchFilters';
import { usePrevious } from 'ha/helpers/hooks/usePrevious';
import { addMonths, normalizeDate } from '@ha/date';
import { getSearchQuery } from './selectors/mapSelectors';

import {
  SearchFiltersContextValue,
  SearchFiltersContext,
  LocalState,
  EmptyState,
} from './SearchFiltersContext';

interface Props {
  instantaneousFiltering?: boolean;
}

export const SearchFiltersProvider: React.FC<Props> = ({
  children,
  instantaneousFiltering,
}) => {
  const dispatch = useDispatch();
  const filtersCount = useSelector(getFiltersCount);

  const globalFilters = useSelector(getFilterValues);
  const searchQuery = useSelector(getSearchQuery);

  const initialLocalState: LocalState = React.useMemo(
    () => ({
      ...globalFilters,
      place: searchQuery,
    }),
    [globalFilters, searchQuery],
  );
  const prevInitialLocalState = usePrevious(initialLocalState);

  const [localState, setLocalState] =
    React.useState<LocalState>(initialLocalState);

  React.useEffect(() => {
    if (
      prevInitialLocalState === undefined ||
      isEqual(initialLocalState, prevInitialLocalState)
    ) {
      return;
    }
    setLocalState(prevState =>
      isEqual(initialLocalState, prevState) ? prevState : initialLocalState,
    );
  }, [initialLocalState, prevInitialLocalState]);

  const getPriceParamsSelector = React.useMemo(
    () => getConvertedPriceFilterParams(localState.currency),
    [localState.currency],
  );

  const {
    currencyRates,
    priceMin,
    priceMax,
    trimmedPriceMin,
    trimmedPriceMax,
    trimmedPriceDistribution,
  } = useSelector(getPriceParamsSelector);

  const onChangeDates = React.useCallback((dates: DateFilterValue) => {
    setLocalState(prevState => ({
      ...prevState,
      dates,
    }));
  }, []);

  const onChangeAvailability = React.useCallback(
    (availability: AvailabilityFilterValue) => {
      const startDate = normalizeDate(
        new Date(availability.moveIn.value).toISOString(),
      );
      const endDate = availability.duration
        ? normalizeDate(addMonths(startDate, availability.duration))
        : null;

      onChangeDates({ startDate, endDate });
    },
    [onChangeDates],
  );

  const onChangeTypes = React.useCallback((types: TypeFilterValue) => {
    setLocalState(prevState => ({
      ...prevState,
      types,
      // reset rooms if apartment type is not selected
      rooms:
        prevState.types.includes(TypeValue.APARTMENT) &&
        !types.includes(TypeValue.APARTMENT)
          ? { ...prevState.rooms, bedroomCount: [] }
          : prevState.rooms,
      ...(types.includes(TypeValue.STUDENT_HOUSING) && {
        suitableFor: prevState.suitableFor.concat([SuitableForValue.STUDENTS]),
      }),
    }));
  }, []);

  const onChangeRooms = React.useCallback((rooms: RoomsFilterValue) => {
    setLocalState(prevState => ({
      ...prevState,
      rooms: {
        ...prevState.rooms,
        ...rooms,
      },
      // add apartment type if it wasn't selected before
      types: prevState.types.includes(TypeValue.APARTMENT)
        ? prevState.types
        : [...prevState.types, TypeValue.APARTMENT],
    }));
  }, []);

  // handle currency change separately to correctly handle rate conversion for prices
  const onChangePrice = React.useCallback((price: PriceFilterValue) => {
    setLocalState(prevState => ({
      ...prevState,
      price,
    }));
  }, []);

  const onChangeCurrency = React.useCallback(
    (currency: string) => {
      setLocalState(prevState => {
        const {
          price: { min, max },
          currency: prevCurrency,
        } = prevState;

        const rate = currencyRates[currency] / currencyRates[prevCurrency];

        return {
          ...prevState,
          currency,
          price: {
            min: min === null ? null : Math.floor(min * rate),
            max: max === null ? null : Math.floor(max * rate),
          },
        };
      });
    },
    [currencyRates],
  );

  const onChangePropertySize = React.useCallback(
    (propertySize: PropertySizeFilterValue) => {
      setLocalState(prevState => ({
        ...prevState,
        propertySize,
      }));
    },
    [],
  );

  const onChangeFurniture = React.useCallback(
    (furniture: FurnitureFilterValue) => {
      setLocalState(prevState => ({
        ...prevState,
        furniture,
      }));
    },
    [],
  );

  const onChangeBills = React.useCallback((bills: BillsFilterValue) => {
    setLocalState(prevState => ({
      ...prevState,
      bills,
    }));
  }, []);

  const onChangeSuitableFor = React.useCallback(
    (suitableFor: SuitableForFilterValue) => {
      setLocalState(prevState => ({
        ...prevState,
        suitableFor,
      }));
    },
    [],
  );

  const onChangeGender = React.useCallback((gender: GenderFilterValue) => {
    setLocalState(prevState => ({
      ...prevState,
      gender,
    }));
  }, []);

  const onChangeRules = React.useCallback((rules: RulesFilterValue) => {
    setLocalState(prevState => ({
      ...prevState,
      rules,
    }));
  }, []);

  const onChangeRegistration = React.useCallback(
    (registration: RegistrationFilterValue) => {
      setLocalState(prevState => ({
        ...prevState,
        registration,
      }));
    },
    [],
  );

  const onChangeRecentlyAdded = React.useCallback(
    (recentlyAdded: RecentlyAddedFilterValue) => {
      setLocalState(prevState => ({
        ...prevState,
        recentlyAdded,
      }));
    },
    [],
  );

  const onChangeSearchAreaFilter = React.useCallback(
    (searchArea: SearchAreaFilterValue) => {
      setLocalState(prevState => ({
        ...prevState,
        searchArea,
      }));
    },
    [],
  );

  const onChangeRadius = React.useCallback((radius: RadiusFilterValue) => {
    setLocalState(prevState => ({
      ...prevState,
      radius,
    }));
  }, []);

  const onChangePlace = React.useCallback((place: string) => {
    setLocalState(prevState => ({
      ...prevState,
      place,
    }));
  }, []);

  const onChangeContractType = React.useCallback(
    (contractType: ContractTypeFilterValue) => {
      setLocalState(prevState => ({
        ...prevState,
        contractType,
      }));
    },
    [],
  );

  const onChangeFacilities = React.useCallback(
    (facilities: FacilitiesFilterValue) => {
      setLocalState(prevState => ({
        ...prevState,
        facilities,
      }));
    },
    [],
  );

  const onChangeAmenities = React.useCallback(
    (amenities: AmenitiesFilterValue) => {
      setLocalState(prevState => ({
        ...prevState,
        amenities,
      }));
    },
    [],
  );

  const clearFilters = React.useCallback(
    (filtersToKeep: Array<keyof LocalState> = []) => {
      setLocalState(prevState => {
        const filtersToRemove: Array<keyof LocalState> = [
          'place',
          'searchArea',
          ...filtersToKeep,
        ];

        return {
          ...EmptyState,
          // some filters should not be cleared
          ...pick(prevState, filtersToRemove),
        };
      });
    },
    [],
  );

  const isFilterEmpty = React.useCallback(
    (filterName: keyof LocalState) =>
      isEqual(localState[filterName], EmptyState[filterName]),
    [localState],
  );

  const applyFilters = React.useCallback(() => {
    dispatch(
      onChangeFilters({
        ...localState,
        // we operate with trimmed limits on filter level, untrim the values when applying to global state
        price: {
          min:
            localState.price.min === trimmedPriceMin
              ? priceMin
              : localState.price.min,
          max:
            localState.price.max === trimmedPriceMax
              ? priceMax
              : localState.price.max,
        },
      }),
    );
  }, [
    dispatch,
    localState,
    priceMin,
    priceMax,
    trimmedPriceMin,
    trimmedPriceMax,
  ]);

  const prevLocalState = usePrevious(localState);
  React.useEffect(() => {
    // skip effect on first render or state didn't change
    if (
      !instantaneousFiltering ||
      prevLocalState === undefined ||
      prevLocalState === localState
    ) {
      return;
    }

    applyFilters();
  }, [instantaneousFiltering, localState, prevLocalState, applyFilters]);

  const value = React.useMemo<SearchFiltersContextValue>(
    () => ({
      localState,
      filtersCount,
      priceParams: {
        priceMin: trimmedPriceMin,
        priceMax: trimmedPriceMax,
        priceDistribution: trimmedPriceDistribution,
      },
      onChangeDates,
      onChangeAvailability,
      onChangeTypes,
      onChangeRooms,
      onChangePrice,
      onChangeCurrency,
      onChangeFurniture,
      onChangeBills,
      onChangeSuitableFor,
      onChangeGender,
      onChangeRules,
      onChangeRegistration,
      onChangeRecentlyAdded,
      onChangeSearchAreaFilter,
      onChangeRadius,
      onChangePlace,
      onChangeContractType,
      onChangePropertySize,
      onChangeFacilities,
      onChangeAmenities,
      clearFilters,
      isFilterEmpty,
      applyFilters,
    }),
    [
      localState,
      filtersCount,
      trimmedPriceMin,
      trimmedPriceMax,
      trimmedPriceDistribution,
      onChangeDates,
      onChangeTypes,
      onChangeRooms,
      onChangePrice,
      onChangeCurrency,
      onChangeFurniture,
      onChangeBills,
      onChangeSuitableFor,
      onChangeGender,
      onChangeRules,
      onChangeRegistration,
      onChangeRecentlyAdded,
      onChangeSearchAreaFilter,
      onChangeRadius,
      onChangePlace,
      onChangeContractType,
      onChangePropertySize,
      onChangeFacilities,
      onChangeAvailability,
      onChangeAmenities,
      clearFilters,
      isFilterEmpty,
      applyFilters,
    ],
  );

  return (
    <SearchFiltersContext.Provider value={value}>
      {children}
    </SearchFiltersContext.Provider>
  );
};
